/** Dependency-free SVG line plot: log/lin x, auto-scaled y, SI tick labels. */
import { siFormat } from "../lib/format";
import { linTicks, logTicks } from "../lib/ticks";

export interface Series {
  y: Float64Array | number[];
  color: string;
  label?: string;
}

export function Plot(props: {
  title: string;
  x: Float64Array;
  series: Series[];
  logX: boolean;
  yUnit: string;
  height?: number;
}) {
  const W = 320;
  const H = props.height ?? 180;
  const ML = 54;
  const MR = 8;
  const MT = 20;
  const MB = 26;
  const iw = W - ML - MR;
  const ih = H - MT - MB;
  const n = props.x.length;
  if (n < 2) return <div class="plot empty">no data</div>;

  const x0 = props.x[0]!;
  const x1 = props.x[n - 1]!;
  const tx = (x: number) => (props.logX ? (Math.log10(x) - Math.log10(x0)) / (Math.log10(x1) - Math.log10(x0)) : (x - x0) / (x1 - x0));

  let lo = Infinity;
  let hi = -Infinity;
  for (const s of props.series) {
    for (let i = 0; i < n; i++) {
      const v = s.y[i]!;
      if (Number.isFinite(v)) {
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
  }
  if (!(hi > lo + Math.abs(hi) * 1e-9)) {
    // Flat series: pad relative to the value so it sits mid-plot.
    const p = Math.max(Math.abs(hi) * 0.05, 1e-30);
    lo -= p;
    hi += p;
  } else {
    const pad = (hi - lo) * 0.06;
    lo -= pad;
    hi += pad;
  }
  const ty = (y: number) => (y - lo) / (hi - lo);

  const px = (x: number) => ML + tx(x) * iw;
  const py = (y: number) => MT + (1 - ty(y)) * ih;

  const xTicks = props.logX ? logTicks(x0, x1) : linTicks(x0, x1, 5);
  const yTicks = linTicks(lo, hi, 5);

  const path = (s: Series) => {
    let d = "";
    for (let i = 0; i < n; i++) {
      const v = s.y[i]!;
      if (!Number.isFinite(v)) continue;
      d += `${d ? " L" : "M"} ${px(props.x[i]!).toFixed(1)} ${py(v).toFixed(1)}`;
    }
    return d;
  };

  return (
    <div class="plot">
      <svg viewBox={`0 0 ${W} ${H}`}>
        <text x={ML} y={12} class="plot-title">
          {props.title} {props.yUnit ? `[${props.yUnit}]` : ""}
        </text>
        {props.series.some((s) => s.label) && (
          <g>
            {props.series.map((s, i) => (
              <g key={i} transform={`translate(${W - MR - 86}, ${8 + i * 12})`}>
                <line x1={0} y1={0} x2={14} y2={0} stroke={s.color} stroke-width={2} />
                <text x={18} y={3} class="plot-tick">
                  {s.label}
                </text>
              </g>
            ))}
          </g>
        )}
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line x1={ML} y1={py(t)} x2={W - MR} y2={py(t)} class="plot-grid" />
            <text x={ML - 4} y={py(t) + 3} text-anchor="end" class="plot-tick">
              {siFormat(t, "", 3).trim()}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <g key={`x${t}`}>
            <line x1={px(t)} y1={MT} x2={px(t)} y2={H - MB} class="plot-grid" />
            <text x={px(t)} y={H - MB + 14} text-anchor="middle" class="plot-tick">
              {siFormat(t, "", 2).trim()}
            </text>
          </g>
        ))}
        <rect x={ML} y={MT} width={iw} height={ih} class="plot-frame" />
        {props.series.map((s, i) => (
          <path key={i} d={path(s)} fill="none" stroke={s.color} stroke-width={1.6} />
        ))}
        <text x={W - MR} y={H - 4} text-anchor="end" class="plot-tick plot-unit">
          Hz
        </text>
      </svg>
    </div>
  );
}
